/**
 * TodoSchedulerService — daemon-side ticker that auto-initiates scheduled todos.
 *
 * Every 60 seconds: fetch todos where scheduled_at <= now and status = 'open'
 * for this daemon's serverId, claim each one (status → in_progress), spawn an
 * agent, link it back to the todo, and materialise a cloud tab.
 *
 * Claim mechanism: the auth-server's GET /daemon/todos/due already filters by
 * scheduleTargetHostId = this daemon's serverId, so two daemons can never race
 * on the same todo. An in-process Set guards against the same tick overlapping
 * itself (ticker fires every 60s; each spawn is awaited serially within the
 * tick, but the tick is fire-and-forgot so a slow spawn could theoretically
 * overlap the next tick).
 */

import { existsSync } from "node:fs";
import type { Logger } from "pino";
import type { AgentManager } from "./agent/agent-manager.js";
import type { AgentSessionConfig } from "./agent/agent-sdk-types.js";
import type { AuthServerTodo, AuthServerTodoClient } from "./auth-server-client.js";

const TICKER_INTERVAL_MS = 60_000;

function composeTodoPrompt(todo: AuthServerTodo): string {
  const parts: string[] = [
    [
      "Start working on this todo immediately. Do NOT ask clarifying questions —",
      "the title, notes, and subtasks below are the full brief. Only stop to ask",
      "if you hit a genuine blocker that cannot be resolved from this context or",
      "from reading the codebase.",
    ].join(" "),
    todo.text,
  ];

  const notes = todo.notes?.trim();
  if (notes) parts.push(notes);

  const pendingSubs = todo.subtodos.filter((s) => !s.completedAt);
  const doneSubs = todo.subtodos.filter((s) => s.completedAt);
  if (todo.subtodos.length > 0) {
    const lines = ["Subtasks:"];
    for (const s of pendingSubs) lines.push(`- [ ] ${s.text}`);
    for (const s of doneSubs) lines.push(`- [x] ${s.text}`);
    parts.push(lines.join("\n"));
  }

  if (pendingSubs.length >= 2) {
    parts.push(
      [
        "If the pending subtasks above are independent of each other, fan them",
        "out by calling `mcp__appostle__handoff` once per subtask — each spawned",
        "agent works in parallel. If they're sequential or share state, do them",
        "yourself in this session.",
      ].join(" "),
    );
  }

  parts.push(
    [
      "When you're done — or hit a blocker that needs the user's input — call",
      `\`mcp__appostle__complete_todo\` with todoId \`${todo.id}\` and a \`report\``,
      "arg (1–3 sentences summarising what shipped or why you stopped). Do NOT",
      "ask the user first — just call it. The todo will flip to completed and the",
      "report will be stored in the Details pane.",
    ].join(" "),
  );

  return parts.join("\n\n");
}

export interface TodoSchedulerOptions {
  serverId: string;
  ownerUserId: string;
  ownerUsername?: string | null;
  authServerTodoClient: AuthServerTodoClient;
  agentManager: AgentManager;
  logger: Logger;
  /**
   * Fires after a todo-initiated spawn creates an agent. Lets bootstrap.ts
   * materialise a cloud tab so the session is visible in the owner's tab strip.
   * Fire-and-forget; errors are caught by the service.
   */
  onAgentSpawned?: (params: {
    agentId: string;
    cwd: string;
    ownerUserId: string;
    title: string | null;
    provider: string;
  }) => Promise<void>;
  /**
   * Fires after the scheduler mutates a todo's status (open → in_progress).
   * Bootstrap supplies this to broadcast the change to all subscribed sessions.
   * `cwd` is the todo's `createdByCwd`.
   */
  onTodoMutated?: (cwd: string) => void;
}

export class TodoSchedulerService {
  private readonly serverId: string;
  private readonly ownerUserId: string;
  private readonly ownerUsername: string | null;
  private readonly authServerTodoClient: AuthServerTodoClient;
  private readonly agentManager: AgentManager;
  private readonly logger: Logger;
  private readonly onAgentSpawned?: TodoSchedulerOptions["onAgentSpawned"];
  private readonly onTodoMutated?: TodoSchedulerOptions["onTodoMutated"];
  private readonly processingIds = new Set<string>();
  private tickTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: TodoSchedulerOptions) {
    this.serverId = options.serverId;
    this.ownerUserId = options.ownerUserId;
    this.ownerUsername = options.ownerUsername ?? null;
    this.authServerTodoClient = options.authServerTodoClient;
    this.agentManager = options.agentManager;
    this.logger = options.logger.child({ module: "todo-scheduler" });
    this.onAgentSpawned = options.onAgentSpawned;
    this.onTodoMutated = options.onTodoMutated;
  }

  start(): void {
    if (this.tickTimer) return;
    void this.tick().catch((err) => {
      this.logger.error({ err }, "todo-scheduler initial tick failed");
    });
    const timer = setInterval(() => {
      void this.tick().catch((err) => {
        this.logger.error({ err }, "todo-scheduler tick failed");
      });
    }, TICKER_INTERVAL_MS);
    (timer as unknown as { unref?: () => void }).unref?.();
    this.tickTimer = timer;
  }

  stop(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  async tick(): Promise<void> {
    let dueTodos: AuthServerTodo[];
    try {
      const result = await this.authServerTodoClient.getDueTodos();
      dueTodos = result.todos;
    } catch (err) {
      this.logger.warn({ err }, "todo-scheduler: getDueTodos failed, skipping tick");
      return;
    }

    if (dueTodos.length > 0) {
      this.logger.info({ count: dueTodos.length }, "todo-scheduler: due todos found");
    }

    for (const todo of dueTodos) {
      if (this.processingIds.has(todo.id)) {
        this.logger.debug({ todoId: todo.id }, "todo-scheduler: already processing, skipping");
        continue;
      }

      // Skip if already linked to a live agent on this daemon.
      if (todo.linkedAgentId) {
        const liveAgent = this.agentManager.getAgentSnapshot(todo.linkedAgentId);
        if (liveAgent) {
          this.logger.debug(
            { todoId: todo.id, agentId: todo.linkedAgentId },
            "todo-scheduler: todo already has a live agent, skipping",
          );
          continue;
        }
      }

      // Skip todos that are still blocked by an incomplete blocker.
      if (todo.blockedByTodoId) {
        this.logger.debug(
          { todoId: todo.id, blockedByTodoId: todo.blockedByTodoId },
          "todo-scheduler: todo still has a blockedByTodoId, skipping",
        );
        continue;
      }

      // Skip todos without a cwd — we can't spawn without a working directory.
      if (!todo.createdByCwd) {
        this.logger.warn(
          { todoId: todo.id },
          "todo-scheduler: todo has no createdByCwd, cannot spawn — skipping",
        );
        continue;
      }

      // Skip todos whose cwd no longer exists on disk.
      if (!existsSync(todo.createdByCwd)) {
        this.logger.warn(
          { todoId: todo.id, cwd: todo.createdByCwd },
          "todo-scheduler: createdByCwd directory missing, reverting to open",
        );
        void this.revertToOpen(
          todo.id,
          new Error(`Working directory no longer exists: ${todo.createdByCwd}`),
        );
        continue;
      }

      this.processingIds.add(todo.id);
      void this.processTodo(todo, todo.createdByCwd).finally(() => {
        this.processingIds.delete(todo.id);
      });
    }
  }

  private async processTodo(todo: AuthServerTodo, cwd: string): Promise<void> {
    const log = this.logger.child({ todoId: todo.id, text: todo.text.slice(0, 60) });

    // Claim: transition open → in_progress before spawning.
    // This is the idempotency guard: if the daemon restarts and fetches the
    // same todo again (it will have been re-queued as open on startup), the
    // second daemon would already see in_progress (or done/linked) and skip.
    try {
      await this.authServerTodoClient.updateTodo(todo.id, {
        status: "in_progress",
        actorHostId: this.serverId,
      });
    } catch (err) {
      log.warn({ err }, "todo-scheduler: failed to claim todo (status→in_progress), skipping");
      return;
    }
    this.onTodoMutated?.(cwd);

    log.info("todo-scheduler: claimed todo, spawning agent");

    const provider = todo.createdByAgentId ? "claude" : "claude"; // TODO: derive from creator if easily available
    const prompt = composeTodoPrompt(todo);

    const config: AgentSessionConfig = {
      provider,
      cwd,
      title: todo.text.slice(0, 120) || null,
      modeId: "bypassPermissions", // hardcoded by explicit user decision
    };

    let agentId: string;
    try {
      const agent = await this.agentManager.createAgent(config, undefined, {
        labels: { "appostle.todo-id": todo.id },
        ownerUserId: this.ownerUserId,
        userId: this.ownerUserId,
        ...(this.ownerUsername ? { username: this.ownerUsername } : {}),
      });
      agentId = agent.id;
      log.info({ agentId }, "todo-scheduler: agent created");
    } catch (err) {
      log.error({ err }, "todo-scheduler: createAgent failed, reverting todo to open");
      await this.revertToOpen(todo.id, err);
      return;
    }

    // Link the agent to the todo. The PATCH handler writes a todo_sessions
    // row and a session_linked activity entry when linkedAgentHostIdForActivity
    // and linkedAgentIdForActivity are provided alongside the linked columns.
    try {
      await this.authServerTodoClient.updateTodo(todo.id, {
        linkedAgentHostId: this.serverId,
        linkedAgentId: agentId,
        linkedAgentHostIdForActivity: this.serverId,
        linkedAgentIdForActivity: agentId,
      });
    } catch (err) {
      log.warn({ err, agentId }, "todo-scheduler: failed to link agent to todo (non-fatal)");
    }

    // Materialise a cloud tab so the session is visible in the owner's tab strip.
    if (this.onAgentSpawned) {
      void this.onAgentSpawned({
        agentId,
        cwd,
        ownerUserId: this.ownerUserId,
        title: todo.text.slice(0, 120) || null,
        provider,
      }).catch((err) => {
        log.warn({ err, agentId }, "todo-scheduler: onAgentSpawned hook failed (non-fatal)");
      });
    }

    // Fire the initial prompt — mirrors how ScheduleService drives the agent.
    try {
      this.agentManager.recordUserMessage(agentId, prompt, { emitState: false });
      void this.agentManager.runAgent(agentId, prompt);
      log.info({ agentId }, "todo-scheduler: agent running");
    } catch (err) {
      log.error({ err, agentId }, "todo-scheduler: runAgent failed (agent already spawned)");
    }
  }

  private async revertToOpen(todoId: string, spawnErr: unknown): Promise<void> {
    try {
      await this.authServerTodoClient.updateTodo(todoId, { status: "open" });
    } catch (revertErr) {
      this.logger.error(
        { revertErr, todoId },
        "todo-scheduler: failed to revert todo back to open after spawn failure",
      );
    }
    try {
      const msg =
        spawnErr instanceof Error
          ? `Scheduler spawn failed: ${spawnErr.message}`
          : "Scheduler spawn failed (unknown error)";
      await this.authServerTodoClient.addTodoComment(todoId, msg, {
        actorHostId: this.serverId,
      });
    } catch {
      // best-effort
    }
  }
}
